import React, { useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { Container, Row } from 'reactstrap';
import {
    Button,
    DateInput,
    Icon,
    InputRadio,
    JustifyContents,
    Label,
    LabelPosition,
    NumericInput,
    TextArea,
    TextInput,
    ToogleSwitch,
} from 'tacho-ui';
import config from '../../../config';
import {
    AddTachoInstallerModel,
    defaultAddTachoInstallerModel,
} from '../../../core/domainModels/versions/AddTachoInstallerModel';
import {
    ManagerPackageComponent,
    managerPackageComponentLabels,
    managerPackageComponents,
} from '../../../core/domainModels/versions/ManagerPackageComponent';
import { TachoReleaseType } from '../../../core/domainModels/versions/TachoReleaseType';
import { ApplicationState } from '../../../state/ApplicationState';
import { DispatchType } from '../../../state/configureStore';
import { addNewInstallerThunk, getAllInstallersThunk } from '../../../state/versions/reducer';
import { TachoModal } from '../../components/TachoModal';
import { TachoEditModalTitle } from '../../components/TachoModal/TachoEditTitle';
import { execExtension, zippedExtension } from '../../pages/versions/models/versionsExtensions';
import './styles.scss';
import { createDateFromTimeZone, dateToStringFormat } from '../../../core/helpers/moment';
import { isMD5Valid } from '../../../core/helpers/md5';
import { isSHA256Valid } from '../../../core/helpers/sha256';

interface AddVersionModalProps {
    isOpen: boolean;
    onToggle: () => void;
}
export const AddVersionModal = (props: AddVersionModalProps) => {
    const { formatMessage } = useIntl();
    const dispatch = useDispatch<DispatchType>();
    const [installerData, setInstallerData] = useState<AddTachoInstallerModel>(defaultAddTachoInstallerModel);
    const onSubmit = () => {
        dispatch(addNewInstallerThunk(installerData))
            .unwrap()
            .then(success => {
                if (success) {
                    props.onToggle();
                    setInstallerData(defaultAddTachoInstallerModel);
                    dispatch(getAllInstallersThunk());
                }
            })
            .catch(err => {
                console.log(err);
            });
    };
    const addInstallerPending = useSelector((state: ApplicationState) => state.versions.addNewInstallerPending);
    const addInstallerError = useSelector((state: ApplicationState) => state.versions.addNewInstallerError);
    const uploadPercent = useSelector((state: ApplicationState) => state.versions.uploadPercent);
    const uploadLoaded = useSelector((state: ApplicationState) => state.versions.uploadLoaded);
    const uploadTotal = useSelector((state: ApplicationState) => state.versions.uploadTotal);
    const fileInput = useRef<HTMLInputElement>(null);
    const handleAddFiles = (event: React.ChangeEvent<HTMLInputElement>): void => {
        if (event.target.files) {
            const file = event.target.files[0];
            if (file) {
                setInstallerData({ ...installerData, file: file });
            }
        }

        if (fileInput.current?.value) {
            fileInput.current.value = '';
        }
    };

    const isManagerPackage = installerData.releaseType === TachoReleaseType.ManagerPackage;
    const selectedComponents = installerData.components ?? [];
    const componentFileInput = useRef<HTMLInputElement>(null);
    const [pickingComponent, setPickingComponent] = useState<ManagerPackageComponent | undefined>(undefined);

    const toggleComponent = (component: ManagerPackageComponent, enabled: boolean) =>
        setInstallerData({
            ...installerData,
            components: enabled
                ? [...selectedComponents, component]
                : selectedComponents.filter(selected => selected !== component),
            componentFiles: enabled ? installerData.componentFiles : { ...installerData.componentFiles, [component]: undefined },
        });

    const pickComponentFile = (component: ManagerPackageComponent) => {
        setPickingComponent(component);
        componentFileInput.current?.click();
    };

    const handleComponentFile = (event: React.ChangeEvent<HTMLInputElement>): void => {
        const file = event.target.files?.[0];
        if (file && pickingComponent) {
            setInstallerData({
                ...installerData,
                componentFiles: { ...installerData.componentFiles, [pickingComponent]: file },
            });
        }

        if (componentFileInput.current?.value) {
            componentFileInput.current.value = '';
        }
    };

    const managerPackageIncomplete =
        selectedComponents.length === 0 || selectedComponents.some(component => !installerData.componentFiles?.[component]);

    return (
        <TachoModal
            size={'md'}
            height={'auto'}
            isOpen={props.isOpen}
            onToggle={() => {
                setInstallerData(defaultAddTachoInstallerModel);
                props.onToggle();
            }}
            onSubmit={onSubmit}
            title={<TachoEditModalTitle titleToTranslation="versionsAddVersion" icon={Icon.DocumentAdd} />}
            submitLabelToTranslate="commonAdd"
            submitIcon={Icon.Add}
            isPending={addInstallerPending}
            errorToTranslate={addInstallerError}
            disableSubmitButton={
                (isManagerPackage ? managerPackageIncomplete : !installerData.file) ||
                !installerData.releaseDate.isValid ||
                installerData.versionMajor === undefined ||
                installerData.versionMinor === undefined ||
                installerData.versionPatch === undefined ||
                !installerData.patchNotes ||
                (!isManagerPackage && !!installerData.MD5 && !isMD5Valid(installerData.MD5)) ||
                (!isManagerPackage && !!installerData.SHA256 && !isSHA256Valid(installerData.SHA256))
            }
        >
            <Container fluid className="d-flex flex-column px-4 gap-3">
                <Label content={formatMessage({ id: 'versionsVersion' })} />
                <Row className="ms-3">
                    <InputRadio
                        justify={JustifyContents.flexStart}
                        labelPosition={LabelPosition.Right}
                        value={installerData.releaseType === TachoReleaseType.FullInstaller}
                        onChange={() => setInstallerData({ ...installerData, releaseType: TachoReleaseType.FullInstaller })}
                        label={formatMessage({ id: TachoReleaseType.FullInstaller })}
                        disabled={addInstallerPending}
                    />

                    <InputRadio
                        justify={JustifyContents.flexStart}
                        labelPosition={LabelPosition.Right}
                        value={installerData.releaseType === TachoReleaseType.Updater}
                        onChange={() => setInstallerData({ ...installerData, releaseType: TachoReleaseType.Updater })}
                        label={formatMessage({ id: TachoReleaseType.Updater })}
                        disabled={addInstallerPending}
                    />

                    <InputRadio
                        justify={JustifyContents.flexStart}
                        labelPosition={LabelPosition.Right}
                        value={installerData.releaseType === TachoReleaseType.UpdaterAsZip}
                        onChange={() => setInstallerData({ ...installerData, releaseType: TachoReleaseType.UpdaterAsZip })}
                        label={formatMessage({ id: TachoReleaseType.UpdaterAsZip })}
                        disabled={addInstallerPending}
                    />

                    <InputRadio
                        justify={JustifyContents.flexStart}
                        labelPosition={LabelPosition.Right}
                        value={installerData.releaseType === TachoReleaseType.ManagerPackage}
                        onChange={() => setInstallerData({ ...installerData, releaseType: TachoReleaseType.ManagerPackage })}
                        label={formatMessage({ id: TachoReleaseType.ManagerPackage })}
                        disabled={addInstallerPending}
                    />
                </Row>

                {!isManagerPackage && (
                    <Row>
                        <Label
                            content={
                                installerData.file && installerData.file
                                    ? installerData.file.name
                                    : formatMessage({ id: 'versionsFileNotChoosen' })
                            }
                        />
                        {addInstallerPending && uploadTotal > 0 && (
                            <span>
                                {formatMessage({ id: 'uploading' })} {Math.round(uploadLoaded / (1024 * 1024))} /{' '}
                                {Math.round(uploadTotal / (1024 * 1024))} MB - {uploadPercent}%
                            </span>
                        )}
                        {!addInstallerPending && (
                            <Button
                                theme="primary"
                                text={formatMessage({ id: 'versionsAddFile' })}
                                onClick={() => fileInput.current?.click()}
                                icon={Icon.OppenFile}
                                className="chooseFileButton"
                            />
                        )}
                    </Row>
                )}

                {isManagerPackage && (
                    <>
                        <Label content={formatMessage({ id: 'versionsComponents' })} />
                        {managerPackageComponents.map(component => (
                            <Row key={component} className="ms-3 align-items-center">
                                <ToogleSwitch
                                    value={selectedComponents.includes(component)}
                                    label={managerPackageComponentLabels[component]}
                                    onChange={value => toggleComponent(component, value)}
                                    disabled={addInstallerPending}
                                />
                                {selectedComponents.includes(component) && (
                                    <>
                                        <Label
                                            content={
                                                installerData.componentFiles?.[component]?.name ??
                                                formatMessage({ id: 'versionsFileNotChoosen' })
                                            }
                                        />
                                        {!addInstallerPending && (
                                            <Button
                                                theme="primary"
                                                text={formatMessage({ id: 'versionsAddFile' })}
                                                onClick={() => pickComponentFile(component)}
                                                icon={Icon.OppenFile}
                                                className="chooseFileButton"
                                            />
                                        )}
                                    </>
                                )}
                            </Row>
                        ))}
                        {addInstallerPending && uploadTotal > 0 && (
                            <span>
                                {formatMessage({ id: 'uploading' })} {Math.round(uploadLoaded / (1024 * 1024))} /{' '}
                                {Math.round(uploadTotal / (1024 * 1024))} MB - {uploadPercent}%
                            </span>
                        )}
                    </>
                )}

                <Row>
                    <NumericInput
                        label={formatMessage({ id: 'versionsVersionMajor' })}
                        value={installerData?.versionMajor}
                        onChange={value => setInstallerData({ ...installerData, versionMajor: value })}
                        disabled={addInstallerPending}
                    />
                </Row>
                <Row>
                    <NumericInput
                        label={formatMessage({ id: 'versionsVersionMinor' })}
                        value={installerData?.versionMinor}
                        onChange={value => setInstallerData({ ...installerData, versionMinor: value })}
                        disabled={addInstallerPending}
                    />
                </Row>
                <Row>
                    <NumericInput
                        label={formatMessage({ id: 'versionsVersionPatch' })}
                        value={installerData?.versionPatch}
                        onChange={value => setInstallerData({ ...installerData, versionPatch: value })}
                        disabled={addInstallerPending}
                    />
                </Row>
                <Row>
                    <DateInput
                        label={formatMessage({ id: 'versionsReleaseDate' })}
                        value={dateToStringFormat(installerData?.releaseDate)}
                        onChange={value => setInstallerData({ ...installerData, releaseDate: createDateFromTimeZone(value) })}
                        locale={config.language}
                        disabled={addInstallerPending}
                    />
                </Row>
                <Row>
                    <TextArea
                        label={formatMessage({ id: 'versionsPatchNotes' })}
                        value={installerData?.patchNotes}
                        onChange={value => setInstallerData({ ...installerData, patchNotes: value as string })}
                        disabled={addInstallerPending}
                    />
                </Row>
                {!isManagerPackage && (
                    <>
                        <Row>
                            <TextInput
                                label={formatMessage({ id: 'versionsMd5' })}
                                value={installerData.MD5}
                                onChange={value => setInstallerData({ ...installerData, MD5: value as string })}
                                disabled={addInstallerPending}
                            />
                        </Row>
                        <Row>
                            <TextInput
                                label={formatMessage({ id: 'versionsSha256' })}
                                value={installerData.SHA256}
                                onChange={value => setInstallerData({ ...installerData, SHA256: value as string })}
                                disabled={addInstallerPending}
                            />
                        </Row>
                    </>
                )}
                <Row>
                    <ToogleSwitch
                        value={installerData.isBeta || false}
                        label={formatMessage({ id: 'versionsBeta' })}
                        onChange={value =>
                            setInstallerData({
                                ...installerData,
                                isBeta: value,
                            })
                        }
                        disabled={addInstallerPending}
                    />
                </Row>
                {!isManagerPackage && (
                    <Row>
                        <TextInput
                            label={formatMessage({ id: 'versionsVariant' })}
                            value={installerData.variant}
                            onChange={value => setInstallerData({ ...installerData, variant: value as string })}
                            disabled={addInstallerPending}
                        />
                    </Row>
                )}
                <input
                    type="file"
                    className="d-none"
                    accept={installerData.releaseType === TachoReleaseType.UpdaterAsZip ? zippedExtension : execExtension}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => handleAddFiles(event)}
                    ref={fileInput}
                />
                <input
                    type="file"
                    className="d-none"
                    accept={zippedExtension}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => handleComponentFile(event)}
                    ref={componentFileInput}
                />
            </Container>
        </TachoModal>
    );
};
